import { useCallback, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, MAX_QUANTITY } from "../../../Features/Cart/cartSlice";
import {
  getProductsList,
  getProductsTypes,
  toastError,
  toastSuccess,
} from "../../../api/services";

import "swiper/css";
import "swiper/css/navigation";
import "react-loading-skeleton/dist/skeleton.css";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import QuickViewModal from "../../QuickView/QuickViewModal";
import ProductCard from "./ProductCard";

const LimitedEdition = () => {
  const cartItems = useSelector((state) => state.cart.items);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [categoryProductsLoading, setCategoryProductsLoading] = useState({});
  const [loadingProductId, setLoadingProductId] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getProductsTypes();
      const data = res?.data?.data || [];
      setCategories(data);
      const loadingState = {};
      data.forEach((cat) => {
        loadingState[cat._type] = true;
      });
      setCategoryProductsLoading(loadingState);
      data.forEach((cat) => fetchProducts(cat._type));
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchProducts = async (_type) => {
    try {
      const dataFilter = {
        type: _type,
        _page: 1,
        _perPage: 10,
      };
      const res = await getProductsList(dataFilter);
      setCategoryProducts((prev) => ({
        ...prev,
        [_type]: res?.data?.data?.products || [],
      }));
    } catch (error) {
      console.error(error);
    } finally {
      // Mark this category as done loading
      setCategoryProductsLoading((prev) => ({
        ...prev,
        [_type]: false,
      }));
    }
  };

  const handleAddToCart = async (product) => {
    // Check if product has variants
    if (!product.product_variants || product.product_variants.length === 0) {
      toastError("No variant available");
      return;
    }

    // For product list, always take the first/default variant
    const defaultVariant = product.product_variants[0];
    const qtyToAdd = 1; // Fixed quantity for list view

    const minDelay = new Promise((resolve) => setTimeout(resolve, 500));
    setLoadingProductId(product.id);

    try {
      // Check stock
      if (defaultVariant.stock < qtyToAdd) {
        toastError(`Only ${defaultVariant.stock} items available`);
        return;
      }

      const productDetails = {
        productID: product.id,
        variantID: defaultVariant.id,
        productName: `${product.name} (${defaultVariant.size || 'Default'})`,
        productPrice: defaultVariant.final_price || defaultVariant.price,
        price: defaultVariant.price,
        discount: defaultVariant.discount || 0,
        discount_amount: defaultVariant.discount_amount || 0,
        frontImg: defaultVariant.images?.[0] || product.image,
        productReviews: product.reviews || "8k+ reviews",
        quantity: qtyToAdd,
      };

      // Check if already in cart
      const existingItem = cartItems.find(
        (item) => item.productID === product.id && item.variantID === defaultVariant.id
      );
      
      const currentQty = existingItem ? existingItem.quantity : 0;
      const newTotal = currentQty + qtyToAdd;

      if (newTotal > MAX_QUANTITY) {
        toastError(`You can only add up to ${MAX_QUANTITY} of this variant`);
        return;
      }

      dispatch(addToCart(productDetails));
      toastSuccess(`${productDetails.productName} added to cart!`);
    } catch (error) {
      console.error("Add to cart error:", error);
      toastError("Failed to add to cart. Please try again.");
    } finally {
      await minDelay;
      setLoadingProductId(null);
    }
  };
 
  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedProduct(null), 300);
  };

  return (
    <div className="new_arrival_product_wrap">
      <div className="container">
        {categories.map((cat) => (
          <div key={cat._type} style={{ marginBottom: 40 }}>
            <div className="deal_of_day_head d-flex justify-content-between heading_line">
              <h3 className="content-title theme_color">{cat.type_label}</h3>
            </div>

            <div style={{ position: "relative" }}>
              {/* If still loading products for this category, show skeleton slides */}
              {categoryProductsLoading[cat._type] ? (
                <Swiper
                  slidesPerView={4}
                  spaceBetween={10}
                  loop={false}
                  navigation={false} // no navigation while loading
                  modules={[Navigation]}
                  breakpoints={{
                    320: { slidesPerView: 1 },
                    576: { slidesPerView: 2 },
                    768: { slidesPerView: 3 },
                    1024: { slidesPerView: 4 },
                  }}
                >
                  {Array(4)
                    .fill(0)
                    .map((_, idx) => (
                      <SwiperSlide key={idx}>
                        <div className="poroduct_item_block">
                          <div
                            className="product_img_block"
                            style={{
                              height: "180px",
                              position: "relative",
                              borderRadius: "8px",
                              overflow: "hidden",
                            }}
                          >
                            <Skeleton
                              height="100%"
                              width="100%"
                              style={{ position: "absolute", top: 0, left: 0 }}
                            />
                          </div>
                          <div
                            className="product_details_block"
                            style={{
                              textAlign: "center",
                              marginTop: "10px",
                              marginBottom: "10px",
                            }}
                          >
                            <Skeleton height={15} width="90%" />
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "10px",
                                marginTop: "10px",
                              }}
                            >
                              <Skeleton height={30} width={100} />
                              <Skeleton height={30} width={100} />
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                </Swiper>
              ) : (
                <>
                  {/* Real products Swiper */}
                  <Swiper
                    slidesPerView={4}
                    spaceBetween={15}
                    loop={(categoryProducts[cat._type] || []).length > 4}
                    navigation={{
                      prevEl: `.deal-prev-${cat._type}`,
                      nextEl: `.deal-next-${cat._type}`,
                    }}
                    modules={[Navigation]}
                    breakpoints={{
                      320: { slidesPerView: 1 },
                      576: { slidesPerView: 2 },
                      768: { slidesPerView: 3 },
                      1024: { slidesPerView: 4 },
                    }}
                  >
                    {(categoryProducts[cat._type] || []).map((item) => {
                      return (
                        <SwiperSlide key={item.id}>
                          <ProductCard
                            product={item}
                            onAddToCart={handleAddToCart}
                            isLoading={loadingProductId === item.id}
                          />
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                  {categoryProducts.length > 5 && (
                    <>
                      {/* Navigation buttons – unique classes per category */}
                      <div
                        className={`deal-prev-${cat._type}`}
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "-5px",
                          transform: "translateY(-50%)",
                          zIndex: 10,
                          background: "#fff",
                          padding: "8px",
                          borderRadius: "50%",
                          cursor: "pointer",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <GoChevronLeft size={20} />
                      </div>
                      <div
                        className={`deal-next-${cat._type}`}
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "-5px",
                          transform: "translateY(-50%)",
                          zIndex: 10,
                          background: "#fff",
                          padding: "8px",
                          borderRadius: "50%",
                          cursor: "pointer",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <GoChevronRight size={20} />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {selectedProduct && (
        <QuickViewModal
          product={selectedProduct}
          isOpen={showModal}
          onClose={handleCloseModal}
          onAddToCart={handleAddToCart}
          cartItems={[]}
        />
      )}
    </div>
  );
};

export default LimitedEdition;
